'use client';

import * as React from 'react';
import { cx, toneClass, type MotionTone } from '../tone';

export interface LayerNode {
  label?: string;
  sub?: string;
  tone?: MotionTone;
  /** Panel background (CSS) — a per-host dusk wash; no token covers these. */
  bg?: string;
}

export interface LayerTransitionProps {
  /** Outgoing host. */
  from?: LayerNode;
  /** Incoming host. */
  to?: LayerNode;
  /** Frame width — px number or any CSS length (e.g. `'100%'`). @default 220 */
  width?: number | string;
  /** Frame height — px number or any CSS length (e.g. `'100%'`). @default 150 */
  height?: number | string;
  /**
   * One-shot mode: play the crossfade + wipe exactly once (settling on the
   * incoming `to` host) instead of the default ambient loop. Used when the atom
   * is wired into a real host switch as an enter transition — see
   * `HostTransitionMount`. Default `false` keeps the gallery's ambient loop.
   * @default false
   */
  oneShot?: boolean;
  /**
   * Fired once the one-shot run finishes (the incoming-panel animation ends).
   * Only meaningful with `oneShot`; lets a mount self-unmount the overlay.
   * Never fires in the ambient (looping) default mode.
   */
  onDone?: () => void;
  /** Extra classes on the root. */
  className?: string;
  /** Inline style on the root. */
  style?: React.CSSProperties;
}

const DEFAULT_FROM: LayerNode = {
  label: 'LOCI',
  sub: 'NATIONAL · APEX',
  tone: 'amber',
  bg: 'linear-gradient(180deg,var(--loci-layer-apex-top),var(--loci-layer-bottom))',
};
const DEFAULT_TO: LayerNode = {
  label: 'LOCI · CLT',
  sub: '35.2271° N · 80.8431° W',
  tone: 'teal',
  bg: 'linear-gradient(180deg,var(--loci-layer-clt-top),var(--loci-layer-bottom))',
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 700,
  fontSize: 15,
  color: 'var(--lm-accent-glow)',
};
const subStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  letterSpacing: '.1em',
  color: 'var(--fg-3)',
  marginTop: 6,
};

/**
 * `LayerTransition` — crossing hosts is a place change, so the accent
 * crossfades (amber ↔ teal ↔ ember) behind a single light-wipe while the
 * coordinate updates. Confirms "you've moved" without a hard cut. Decorative
 * (`aria-hidden`).
 *
 * Two modes:
 *   - Default: an ambient looping demo (gallery / Storybook).
 *   - `oneShot`: a single enter pass that settles on the incoming `to` host and
 *     fires `onDone` when finished. This is the mode wired into the real
 *     Apex ⇄ City ⇄ App ⇄ Studio host switch (WEB-473) — host switches are
 *     cross-host hard navigations, so the new host plays this once on mount
 *     (see `HostTransitionMount`). Reduced motion is gated centrally in
 *     `loci-motion.css`.
 */
export function LayerTransition({
  from = DEFAULT_FROM,
  to = DEFAULT_TO,
  width = 220,
  height = 150,
  oneShot = false,
  onDone,
  className,
  style,
}: LayerTransitionProps): React.ReactElement {
  // In one-shot mode the incoming panel (`--b`) runs the single keyframe pass;
  // its `animationend` is the canonical "transition finished" signal. The wipe
  // and outgoing panel run the same one-shot duration but settle to their end
  // state via `forwards` fill, so keying off `--b` alone avoids firing `onDone`
  // multiple times (one per animated element). A native `ref` listener (rather
  // than React's `onAnimationEnd` synthetic) is used because it observes only
  // the panel's *own* `animationend` (no bubbling from child anims) and is the
  // direct DOM signal. Under reduced motion the central gate in `loci-motion.css`
  // collapses the run to ~0ms so this still fires promptly — but the mount also
  // guards reduced-motion before rendering at all.
  const incomingRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!oneShot) return;
    const el = incomingRef.current;
    if (!el) return;
    const handle = (event: AnimationEvent): void => {
      if (event.target !== el) return; // ignore bubbled child animations
      onDone?.();
    };
    el.addEventListener('animationend', handle);
    return () => el.removeEventListener('animationend', handle);
  }, [oneShot, onDone]);

  return (
    <div
      className={cx('lm-layers', oneShot && 'lm-layers--once', className)}
      style={{ width, height, ...style }}
      aria-hidden="true"
    >
      <div
        className={cx('lm-layers__panel', 'lm-layers__panel--a', toneClass(from.tone))}
        style={{ background: from.bg }}
      >
        <div>
          <div style={labelStyle}>{from.label}</div>
          <div style={subStyle}>{from.sub}</div>
        </div>
      </div>
      <div
        ref={incomingRef}
        className={cx('lm-layers__panel', 'lm-layers__panel--b', toneClass(to.tone))}
        style={{ background: to.bg }}
      >
        <div>
          <div style={labelStyle}>{to.label}</div>
          <div style={subStyle}>{to.sub}</div>
        </div>
      </div>
      <div className="lm-layers__wipe" />
    </div>
  );
}
